import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';

import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { AccessRoleDTO, AccessRoleFilterDTO, ResumeDTO, ResumeFilterDTO, ValueListDTO, ValueListFilterDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, IChipsSettingProps, CheckBoxComp } from '../../../shared/components';
import { ResumeMaintenance } from './ResumeMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';
import { Import } from './Import';
import { Comment } from '../../infosphere/comment/Comment';
import { CustomResume } from '../vacancy/CustomResume';
import { useTranslation } from 'react-i18next';

interface IResume {
    data: any;
}

export const Resume: React.FC<IResume> = ({ data }) => {

    const [jobTitleList, setJobTitleList] = useState<ValueListDTO[]>([]);
    const [seniorityList, setSeniorityeList] = useState<ValueListDTO[]>([]);
    const [statusList, setStatusList] = useState<ValueListDTO[]>([]);
    const [resumeList, setResumeList] = useState<ResumeDTO[]>([]);
    const [resumeFilterDTO, setResumeFilterDTO] = useState<ResumeFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [importRecord, setImportRecord] = useState('');
    const [comment, setComment] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setResumeFilterDTO({
            ...resumeFilterDTO,
            name: '',
            email: '',
            jobTitleId: '',
            seniorityId: '',
            statusId: '',
            companyId: ''
        } as ResumeFilterDTO);

        handleJobTitleGetAll();
        handleSeniorityGetAll();
        handleStatusGetAll();

        handleGetPermission(ListId.talentsphere.resumeId, setAccessRolePermissionDTO);
    }, []);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync', accessRoleFilterDTO, setActionExecuted, setLoading, undefined, setList);
    }, []);

    useEffect(() => {
        handleResumeGetAllViewAsync();
    }, [resumeFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleResumeGetAllViewAsync();
    }, [actionExecuted]);

    const handleJobTitleGetAll = useCallback(async () => {
        const valueListFilterDTO: ValueListFilterDTO = {
            value: '',
            listId: ListId.talentsphere.vacancy.jobTitleId,
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setJobTitleList, showMessage);
    }, []);

    const handleSeniorityGetAll = useCallback(async () => {
        const valueListFilterDTO: ValueListFilterDTO = {
            value: '',
            listId: ListId.talentsphere.vacancy.seniorityId,
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setSeniorityeList, showMessage);
    }, []);

    const handleStatusGetAll = useCallback(async () => {
        const valueListFilterDTO: ValueListFilterDTO = {
            value: '',
            listId: ListId.talentsphere.resume.statusId,
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setStatusList, showMessage);
    }, []);

    const handleResumeGetAllViewAsync = useCallback(async () => {
        if (resumeFilterDTO === undefined)
            return;
        if (accessRolePermissionDTO?.allowGetAllView == undefined)
            return;
        if (!accessRolePermissionDTO?.allowGetAllView) {
            showMessage(t('message.allowGetAllView'), 'info');
            return;
        }

        GenericService.GetAllViewAsync1('talentsphere.resume', resumeFilterDTO, setActionExecuted, setLoading, setResumeList, showMessage, true);
    }, [resumeFilterDTO, accessRolePermissionDTO]);

    const handleClearFilterClick = () => {
        setResumeFilterDTO({
            ...resumeFilterDTO,
            name: '', email: '',
            jobTitleId: '',
            seniorityId: '',
            statusId: '',
            companyId: ''
        } as ResumeFilterDTO);
    };

    function handleSearchClick() {
        handleResumeGetAllViewAsync();
    }

    const handleNew = () => {
        if (!accessRolePermissionDTO?.allowAdd) {
            showMessage(t('message.allowAdd'), 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setImportRecord('');
        setComment('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        if (!accessRolePermissionDTO?.allowGetById) {
            showMessage(t('message.allowGetById'), 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setImportRecord('');
        setComment('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        if (!accessRolePermissionDTO?.allowDelete) {
            showMessage(t('message.allowDelete'), 'info');
            return;
        }
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setImportRecord('');
        setComment('');
        setIsOpenMaintenance(true);
    };

    const handleImport = () => {
        if (!accessRolePermissionDTO?.allowAdd) {
            showMessage(t('message.allowAdd'), 'info');
            return;
        }
        setNewRecord('');
        setEditRecord('');
        setDeleteRecord('');
        setImportRecord('data');
        setComment('');
        setIsOpenMaintenance(true);
    };

    const handleComment = (data: any) => {
        setNewRecord('');
        setEditRecord('');
        setDeleteRecord('');
        setImportRecord('');
        setComment(data.id);
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: t('resume.button.new'), icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
        // { id: 'import', text: t('resume.button.import'), icon: 'publish_outlined', iconColor: 'primary', setAction: handleImport, isClose: false },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: t('resume.cardActions.viewDetails'), icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'comment', toolTip: t('resume.cardActions.comments'), icon: 'comment_outlined', iconColor: 'success', setAction: handleComment },
        { id: 'delete', toolTip: t('resume.cardActions.delete'), icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: t('resume.chipFilters.totalRecords'), value: resumeList.length },
        { label: t('resume.chipFilters.name'), value: resumeFilterDTO?.name },
        { label: t('resume.chipFilters.code'), value: resumeFilterDTO?.code },
        { label: t('resume.chipFilters.email'), value: resumeFilterDTO?.email },
        { label: t('resume.chipFilters.jobTitle'), value: jobTitleList.find(x => x.id === resumeFilterDTO?.jobTitleId)?.value },
        { label: t('resume.chipFilters.status'), value: statusList.find(x => x.id === resumeFilterDTO?.statusId)?.value },
        // { label: t('resume.chipFilters.course'), value: statusList.find(x => x.id === resumeFilterDTO?.coursesAndCertificates)?.value },
    ];

    const chipStatus: IChipsSettingProps[] = [
        { value: t('resume.chipStatus.available'), iconName: 'person_add_alt_outlined', iconColor: 'success' },
        { value: t('resume.chipStatus.unavailable'), iconName: 'person_add_disabled_outlined', iconColor: 'error' },
        { value: t('resume.chipStatus.hired'), iconName: 'person_outlined', iconColor: 'primary' },
        { value: t('resume.chipStatus.competingForTheVacancy'), iconName: 'manage_accounts_outlined', iconColor: 'info' },
        { value: t('resume.chipStatus.deletionRequested'), iconName: 'manage_accounts_outlined', iconColor: 'warning' },
    ];

    const chipFlag: IChipsSettingProps[] = [
        { value: t('resume.chipFlag.true'), iconName: 'task_alt_outlined', iconColor: 'error' },
        { value: t('resume.chipFlag.false'), iconName: 'block_outlined', iconColor: 'success' },
    ];

    const chipUpdatedInformations: IChipsSettingProps[] = [
        { value: t('resume.chipUpdatedInformations.true'), iconName: 'task_alt_outlined', iconColor: 'success' },
        { value: t('resume.chipUpdatedInformations.false'), iconName: 'block_outlined', iconColor: 'warning' },
    ];

    const columns: ICardColumns[] = [
        { field: 'code', label: t('resume.columns.code'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'name', label: t('resume.columns.name'), size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'jobTitle', label: t('resume.columns.jobTitle'), size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'seniority', label: t('resume.columns.seniority'), size: 1, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'status', label: t('resume.columns.status'), size: 2, type: 'text', visible: true, isChip: true, chipSettings: chipStatus, hasFieldLine: false, hasToolTip: true },
        { field: 'updateDateTime', label: t('resume.columns.updateDateTime'), size: 2, type: 'dateTime', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'isUpdatedInformations', label: t('resume.columns.isUpdateInformations'), size: 2, type: 'boolean', visible: true, isChip: true, chipSettings: chipUpdatedInformations, hasFieldLine: false, hasToolTip: true },
        { field: 'isIncomplete', label: t('resume.columns.isIncomplete'), size: 2, type: 'boolean', visible: true, isChip: true, chipSettings: chipFlag, hasFieldLine: false, hasToolTip: true },
        { field: 'isSuspicious', label: t('resume.columns.isSuspicious'), size: 2, type: 'boolean', visible: true, isChip: true, chipSettings: chipFlag, hasFieldLine: false, hasToolTip: true },
        { field: 'isBlackList', label: t('resume.columns.isBlackList'), size: 2, type: 'boolean', visible: true, isChip: true, chipSettings: chipFlag, hasFieldLine: false, hasToolTip: true },

    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title={t('resume.listName')}>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <TextFieldInput
                                id='name'
                                label={t('resume.filter.name')}
                                placeholder={t('resume.filter.name')}
                                setValue={setResumeFilterDTO}
                                value={resumeFilterDTO?.name}
                                obj={resumeFilterDTO}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextFieldInput
                                id='email'
                                label={t('resume.filter.email')}
                                placeholder={t('resume.filter.email')}
                                setValue={setResumeFilterDTO}
                                value={resumeFilterDTO?.email}
                                obj={resumeFilterDTO}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldInput
                                id='code'
                                label={t('resume.filter.code')}
                                placeholder={t('resume.filter.code')}
                                setValue={setResumeFilterDTO}
                                value={resumeFilterDTO?.code}
                                obj={resumeFilterDTO}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='jobTitleId'
                                label={t('resume.filter.jobTitle')}
                                setValue={setResumeFilterDTO}
                                value={resumeFilterDTO?.jobTitleId}
                                obj={resumeFilterDTO}
                                valueList={jobTitleList}
                                hasNoneSelect={true}
                                searchable={true}
                                labelKey='value'
                                valueKey='id'
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='seniorityId'
                                label={t('resume.filter.seniority')}
                                setValue={setResumeFilterDTO}
                                value={resumeFilterDTO?.seniorityId}
                                obj={resumeFilterDTO}
                                valueList={seniorityList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='statusId'
                                label={t('resume.filter.status')}
                                setValue={setResumeFilterDTO}
                                value={resumeFilterDTO?.statusId}
                                obj={resumeFilterDTO}
                                valueList={statusList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <CheckBoxComp
                                id='isIncomplete'
                                label={t('resume.filter.isIncomplete')}
                                setValue={setResumeFilterDTO}
                                objDTO={resumeFilterDTO}
                                defaultChecked={resumeFilterDTO?.isIncomplete}
                                disabled={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <CheckBoxComp
                                id='isSuspicious'
                                label={t('resume.filter.isSuspicious')}
                                setValue={setResumeFilterDTO}
                                objDTO={resumeFilterDTO}
                                defaultChecked={resumeFilterDTO?.isSuspicious}
                                disabled={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CheckBoxComp
                                id='isBlackList'
                                label={t('resume.filter.isBlackList')}
                                setValue={setResumeFilterDTO}
                                objDTO={resumeFilterDTO}
                                defaultChecked={resumeFilterDTO?.isBlackList}
                                disabled={false}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={resumeList}
                        actions={cardActions}
                        hasChildren={false}
                        setRowExpanded={() => void undefined}
                        cardSize={12}
                    />
                </Box>
                {newRecord && (
                    <ResumeMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <ResumeMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'talentsphere.resume'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />)}
                {importRecord && (
                    <Import
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {comment && (
                    <Comment
                        ownerId={comment}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        actionExecuted={actionExecuted}
                    />

                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};
